import React from "react";
import { Formik, Form, Field, useFormikContext } from "formik";
import { Typography, Button } from "@mui/material";
import * as Yup from "yup";
import styles from "../../../walkTestSecondStep.module.css";
import axios from "../../../../../utils/lib/axios";
import {
    sleepLabelStyle,
    sleepOutlineButtonStyle,
    sleepPrimaryButtonStyle,
} from "../../sleepReportTheme";

const validationSchema = Yup.object().shape({
    odiDropPercentage: Yup.string().required(
        "Please select ODI Drop Percentage",
    ),
    odiDropTime: Yup.string().required("Please select ODI drop time"),
    t1Score: Yup.string().required("Please select T1"),
    t2Score: Yup.string().required("Please select T2"),
});

const defaultInitialValues = {
    odiDropPercentage: "",
    odiDropTime: "",
    t1Score: "",
    t2Score: "",
};

const labelStyle = sleepLabelStyle;

const backButtonStyle = {
    ...sleepOutlineButtonStyle,
    width: "196px",
    height: "48px",
};

/** Out-of-flow so validation text does not increase layout height. */
const fieldErrorOverlayBase = {
    position: "absolute",
    left: 0,
    right: 0,
    top: "100%",
    marginTop: "4px",
    display: "flex",
    alignItems: "flex-start",
    gap: "6px",
    color: "#d32f2f",
    fontSize: "12px",
    zIndex: 2,
    pointerEvents: "none",
};

const errorIconStyle = {
    width: 16,
    height: 16,
    borderRadius: "50%",
    backgroundColor: "#d32f2f",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    color: "#fff",
    fontSize: 12,
    fontWeight: 700,
};

const fieldInputAnchorStyle = {
    position: "relative",
    alignSelf: "stretch",
};

const fieldWrapperStyle = {
    flex: "0 0 324px",
    minWidth: "220px",
    maxWidth: "324px",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
};

const rowStyle = {
    display: "flex",
    flexDirection: "row",
    gap: "80px",
    flexWrap: "wrap",
    marginBottom: "40px",
};

const toUtcISOStringFromDateAndTime = (dateStr, timeStr) => {
    if (!dateStr || !timeStr) return "";

    // `date` input gives `YYYY-MM-DD` and `time` input gives `HH:MM`.
    // Build a local datetime (no timezone suffix) then convert to UTC ISO.
    const normalizedTime = timeStr.length === 5 ? `${timeStr}:00` : timeStr;
    const localDate = new Date(`${dateStr}T${normalizedTime}`);
    return localDate.toISOString();
};

function FormValidityWatcher({
    submitAttempted,
    setSubmitAttempted,
    clearToast,
}) {
    const { isValid } = useFormikContext();

    React.useEffect(() => {
        if (submitAttempted && isValid) {
            clearToast();
            setSubmitAttempted(false);
        }
    }, [clearToast, isValid, setSubmitAttempted, submitAttempted]);

    return null;
}

export default function OtherInformationForm({
    initialValues = defaultInitialValues,
    formData,
    onBack,
    innerRef,
    setAlertDetails,
    onNextStep,
    setReportData,
}) {
    const [submitAttempted, setSubmitAttempted] = React.useState(false);

    const clearToast = React.useCallback(() => {
        setAlertDetails?.({
            message: "",
            severity: "error",
            showAlert: false,
            autoClose: true,
        });
    }, [setAlertDetails]);

    const handleVerify = async (values) => {
        try {
            setAlertDetails?.({
                message: "",
                severity: "error",
                showAlert: false,
                autoClose: true,
            });
            console.log(formData);
            const response = await axios.post(
                "/sleep/create-report",
                //TODO: Hardcoded values for now, will be replaced with the actual values later
                {
                    device_id: formData?.device,

                    sleep_start: toUtcISOStringFromDateAndTime(
                        formData.startDate,
                        formData.startTime,
                    ),
                    sleep_end: toUtcISOStringFromDateAndTime(
                        formData.endDate,
                        formData.endTime,
                    ),

                    // sleep_start: formData?.sleep_start,
                    // sleep_end: formData?.sleep_end,
                    // device_id: "e8e1d21c-b6e9-4c2d-b50f-670813d6976f",
                    // sleep_start: `2026-03-07T16:30:00.000Z`,
                    // sleep_end: `2026-03-08T23:30:00.000Z`,
                    odi_drop_percentage: values.odiDropPercentage,
                    odi_drop_time_sec: values.odiDropTime,
                    t1: values.t1Score,
                    t2: values.t2Score,
                },
            );

            setReportData?.(response?.data);
            // Pass selected ODI/T1/T2 values upward so they persist
            // when we navigate back to this step.
            onNextStep?.(values);
        } catch (error) {
            setAlertDetails?.({
                message:
                    error?.response?.data?.message ||
                    "Something went wrong while verifying sleep data. Please try again.",
                severity: "error",
                showAlert: true,
                autoClose: true,
            });
        }
    };

    return (
        <Formik
            innerRef={innerRef}
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={(values) => handleVerify(values)}
            enableReinitialize
            validateOnMount
        >
            {({
                errors,
                touched,
                isSubmitting,
                isValid,
                validateForm,
                setTouched,
                submitForm,
            }) => (
                <Form>
                    <FormValidityWatcher
                        submitAttempted={submitAttempted}
                        setSubmitAttempted={setSubmitAttempted}
                        clearToast={clearToast}
                    />
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "7rem",
                            height: "100%",
                            marginTop: "12px",
                            flexWrap: "nowrap",
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "1rem",
                            }}
                        >
                            {" "}
                            <div style={rowStyle}>
                                <div style={fieldWrapperStyle}>
                                    <Typography
                                        style={{
                                            ...labelStyle,
                                            marginBottom: 0,
                                        }}
                                        className={`${styles.label}`}
                                    >
                                        ODI Drop Percentage (%)
                                    </Typography>
                                    <div style={fieldInputAnchorStyle}>
                                        <Field
                                            name="odiDropPercentage"
                                            as="select"
                                            className={`${styles.sleepFormInputFull} ${
                                                errors.odiDropPercentage &&
                                                touched.odiDropPercentage
                                                    ? styles.inputError
                                                    : ""
                                            }`}
                                        >
                                            <option value="">Select</option>
                                            <option value="1">1%</option>
                                            <option value="2">2%</option>
                                            <option value="3">3%</option>
                                            <option value="4">4%</option>
                                            <option value="5">5%</option>
                                        </Field>
                                        {errors.odiDropPercentage &&
                                            touched.odiDropPercentage && (
                                                <div
                                                    style={
                                                        fieldErrorOverlayBase
                                                    }
                                                >
                                                    <span
                                                        style={errorIconStyle}
                                                    >
                                                        !
                                                    </span>
                                                    {errors.odiDropPercentage}
                                                </div>
                                            )}
                                    </div>
                                </div>
                                <div style={fieldWrapperStyle}>
                                    <Typography
                                        style={{
                                            ...labelStyle,
                                            marginBottom: 0,
                                        }}
                                        className={`${styles.label}`}
                                    >
                                        ODI Drop Time (sec)
                                    </Typography>
                                    <div style={fieldInputAnchorStyle}>
                                        <Field
                                            name="odiDropTime"
                                            as="select"
                                            className={`${styles.sleepFormInputFull} ${
                                                errors.odiDropTime &&
                                                touched.odiDropTime
                                                    ? styles.inputError
                                                    : ""
                                            }`}
                                        >
                                            <option value="">Select</option>
                                            <option value="1">1 second</option>
                                            <option value="2">2 seconds</option>
                                            <option value="3">3 seconds</option>
                                            <option value="5">5 seconds</option>
                                            <option value="6">6 seconds</option>
                                            <option value="7">7 seconds</option>
                                            <option value="8">8 seconds</option>
                                            <option value="9">9 seconds</option>
                                            <option value="10">10 seconds</option>
                                        </Field>
                                        {errors.odiDropTime &&
                                            touched.odiDropTime && (
                                                <div
                                                    style={
                                                        fieldErrorOverlayBase
                                                    }
                                                >
                                                    <span
                                                        style={errorIconStyle}
                                                    >
                                                        !
                                                    </span>
                                                    {errors.odiDropTime}
                                                </div>
                                            )}
                                    </div>
                                </div>
                            </div>
                            <div style={rowStyle}>
                                <div style={fieldWrapperStyle}>
                                    <Typography
                                        style={{
                                            ...labelStyle,
                                            marginBottom: 0,
                                        }}
                                        className={`${styles.label}`}
                                    >
                                        T1 (%)
                                    </Typography>
                                    <div style={fieldInputAnchorStyle}>
                                        <Field
                                            name="t1Score"
                                            as="select"
                                            className={`${styles.sleepFormInputFull} ${
                                                errors.t1Score && touched.t1Score
                                                    ? styles.inputError
                                                    : ""
                                            }`}
                                        >
                                            <option value="">Select</option>
                                            {Array.from(
                                                { length: 21 },
                                                (_, i) => 80 + i,
                                            ).map((v) => (
                                                <option
                                                    key={v}
                                                    value={String(v)}
                                                >
                                                    {v}%
                                                </option>
                                            ))}
                                        </Field>
                                        {errors.t1Score && touched.t1Score && (
                                            <div style={fieldErrorOverlayBase}>
                                                <span style={errorIconStyle}>
                                                    !
                                                </span>
                                                {errors.t1Score}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div style={fieldWrapperStyle}>
                                    <Typography
                                        style={{
                                            ...labelStyle,
                                            marginBottom: 0,
                                        }}
                                        className={`${styles.label}`}
                                    >
                                        T2 (%)
                                    </Typography>
                                    <div style={fieldInputAnchorStyle}>
                                        <Field
                                            name="t2Score"
                                            as="select"
                                            className={`${styles.sleepFormInputFull} ${
                                                errors.t2Score && touched.t2Score
                                                    ? styles.inputError
                                                    : ""
                                            }`}
                                        >
                                            <option value="">Select</option>
                                            {Array.from(
                                                { length: 21 },
                                                (_, i) => 80 + i,
                                            ).map((v) => (
                                                <option
                                                    key={v}
                                                    value={String(v)}
                                                >
                                                    {v}%
                                                </option>
                                            ))}
                                        </Field>
                                        {errors.t2Score && touched.t2Score && (
                                            <div style={fieldErrorOverlayBase}>
                                                <span style={errorIconStyle}>
                                                    !
                                                </span>
                                                {errors.t2Score}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div style={{ marginTop: "auto" }}>
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "flex-end",
                                    gap: "12px",
                                    marginTop: "auto",
                                    paddingTop: "2rem",
                                }}
                            >
                                <Button
                                    type="button"
                                    onClick={onBack}
                                    variant="outlined"
                                    style={backButtonStyle}
                                >
                                    Back
                                </Button>

                                <Button
                                    type="button"
                                    disabled={
                                        isSubmitting ||
                                        (submitAttempted && !isValid)
                                    }
                                    onClick={async () => {
                                        const validationErrors =
                                            await validateForm();
                                        const hasValidationErrors =
                                            Object.keys(validationErrors)
                                                .length > 0;

                                        if (hasValidationErrors) {
                                            setSubmitAttempted(true);
                                            setTouched({
                                                odiDropPercentage: true,
                                                odiDropTime: true,
                                                t1Score: true,
                                                t2Score: true,
                                            });

                                            // const validationMessage = Object.values(
                                            //     validationErrors,
                                            // )
                                            //     .filter(Boolean)
                                            //     .join(", ");

                                            setAlertDetails?.({
                                                message:
                                                    "Please complete all fields.",
                                                severity: "error",
                                                showAlert: true,
                                                autoClose: false,
                                            });
                                            return;
                                        }

                                        setSubmitAttempted(false);
                                        submitForm();
                                    }}
                                    variant="contained"
                                    style={{
                                        ...sleepPrimaryButtonStyle,
                                        width: "196px",
                                        height: "48px",
                                        opacity:
                                            isSubmitting ||
                                            (submitAttempted && !isValid)
                                                ? "0.7"
                                                : "1",
                                    }}
                                >
                                    Next
                                </Button>
                            </div>
                        </div>
                    </div>
                </Form>
            )}
        </Formik>
    );
}
